import { Component, OnInit, Input, Inject, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AsamcoUserService } from '@asamco/base';
import { FormHelpers, showErrorSwal } from '@asamco/utils';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Link } from '../../interfaces/link.interface';
import { AlCompanyService } from '../../services/al-company.service';
import { LinkService } from '../../services/link.service';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTextBoxModule, DxDateBoxModule, DxButtonComponent, DxButtonModule, DxPopupModule, DxTextAreaModule, DxSwitchModule, DxCheckBoxModule } from 'devextreme-angular';
import { NgIf, AsyncPipe } from '@angular/common';
import { DeleteLinkModalComponent } from '../delete-link-modal/delete-link-modal.component';
import { LinkTransferService } from '../../services/link-transfer.service';
import { LinkTransferSerializerOptions } from '../../interfaces/link-transfer-serialization-options.interface';
import { LinkTransferDeserializerOptions } from '../../interfaces/link-transfer-deserialization-options.interface';
import { TooltipCircleComponent } from 'libs/application-link/shared/src/lib/components/tooltip-circle.component';
import { WarningModalMessageService } from '@asamco/application-link/shared';

@Component({
	selector: 'al-link-edit-general',
	templateUrl: './link-edit-general.component.html',
	styleUrls: ['./link-edit-general.component.scss'],
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		ReactiveFormsModule,
		DxTextBoxModule,
		AsValidatorDirective,
		DxDateBoxModule,
		AsyncPipe,
		L10nTranslatePipe,
		DeleteLinkModalComponent,
		DxButtonModule,
		DxPopupModule,
		DxTextAreaModule,
		DxSwitchModule,
		DxCheckBoxModule,
		TooltipCircleComponent
	],
})
export class LinkEditGeneralComponent implements OnInit, OnDestroy {
	/** The input link */
	@Input()
	link: Link;

	/** Form group for general form */
	@Input()
	formGroup: FormGroup;

	@ViewChild(DeleteLinkModalComponent)
	deleteLinkModal: DeleteLinkModalComponent;

	destroy$ = new Subject<void>();

	public formInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public canManageLinkExpiry: boolean = false;
	public canTransferLinks: boolean = false;
	public dateFormat: string = '';

	isLoading = false;

	exportLinkConfigForm!: FormGroup;
	importLinkConfigForm!: FormGroup;

	constructor(
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		private asamcoUserService: AsamcoUserService,
		private companyService: AlCompanyService,
		private linkService: LinkService,
		private router: Router,
		private fb: FormBuilder,
		private linktransferService: LinkTransferService,
		private warningModalMessageService: WarningModalMessageService
	) { }

	ngOnInit(): void {
		this.asamcoUserService.hasAssignedPermission('AL3_LINK_TRANSFER').then(result => (this.canTransferLinks = result));

		this.exportLinkConfigForm = this.fb.group({
			includeFromLogin: [false],
			includeToLogin: [false],
			includeSettings: [false],
			includeEmailInfo: [false],
			useExplicitLinkName: [false],
			includeLogs: [false]
		});

		this.importLinkConfigForm = this.fb.group({
			includeFromLogin: [false],
			includeToLogin: [false],
			includeSettings: [false],
			includeEmailInfo: [false],
			includeLogs: [false],
			serializedLink: ['']
		});

		this.companyService
			.getCompanyDateFormat()
			.pipe(
				switchMap(dateFormat => {
					this.dateFormat = dateFormat;
					return this.linkService.get(this.link.id);
				}),
				switchMap(retrievedLink => {
					this.initializeForm(retrievedLink);
					return this.asamcoUserService.hasAssignedPermission('AL3_LINK_EXPIRY_MANAGE');
				})
			)
			.subscribe((canManageLinkExpiry: boolean) => {
				this.canManageLinkExpiry = canManageLinkExpiry;
				if (this.canManageLinkExpiry) {
					this.formGroup.get('expiryDate').enable({ emitEvent: true });
					this.formGroup.get('excludeFromExpiryNotificationMail').enable({ emitEvent: true });
				}
			});
	}

	private initializeForm(retrievedLink: any) {
		this.formGroup.addControl('name', new FormControl(retrievedLink.name, Validators.required));
		this.formGroup.addControl('expiryDate', new FormControl({ value: retrievedLink.expiryDate, disabled: true }, Validators.required));
		this.formGroup.addControl('excludeFromExpiryNotificationMail', new FormControl({ value: retrievedLink.excludeFromExpiryNotificationMail ?? false, disabled: true }));
		this.formInitialized$.next(true);
	}

	deleteLink(): void {
		this.deleteLinkModal.showAndAwait().pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.deleteLinkById(this.link.id);
		});
	}

	public deleteLinkById(linkId: number): void {
		this.linkService
			.deleteLink(linkId)
			.pipe(
				catchError(err => {
					showErrorSwal(err);
					return of(false);
				})
			)
			.subscribe(
				e => {
					if (e) {
						this.router.navigate(['/']);
						Swal.fire({
							icon: 'success',
							title: 'Succesfully deleted the link',
						});
					}
				},
				error => {
					showErrorSwal(error);
				}
			);
	}

	public save() {
		const result = {};

		if (FormHelpers.validateForm(this.formGroup, result)) {
			return result;
		}

		return null;
	}

	public openExportPanel() {
		if (!this.canTransferLinks) return;
		this.exportPopupVisible = true;
	}

	public openImportPanel() {
		if (!this.canTransferLinks) return;
		this.importPopupVisible = true;
	}

	outputText$: Observable<string>;
	inputText$: BehaviorSubject<string> = new BehaviorSubject<string>('');
	previewText$ = new BehaviorSubject<string>('');

	exportPopupVisible = false;
	importPopupVisible = false;

	copied = false;

	copyOutputText(): void {
		this.outputText$.subscribe(text => {
			if (text) {
				navigator.clipboard.writeText(text).then(() => {
					this.copied = true;
					setTimeout(() => this.copied = false, 1500);
				});
			}
		});
	}

	previewInputText() {
		this.linktransferService.getLinkTransferCodePreview(this.inputText$.value).pipe(
			catchError(err => of('Error: ' + err.message))
		).subscribe(preview => {
			this.previewText$.next(
				typeof preview === 'string' ? preview : JSON.stringify(preview, null, 2)
			);
		});
	}

	runBackendAction() {
		const payload: LinkTransferSerializerOptions = this.exportLinkConfigForm.value;

		payload.linkId = this.link.id;

		this.outputText$ = this.linktransferService.getLinkTransferCode(payload).pipe(
			catchError(err => of('Error: ' + err.message))
		);
	}

	onInputTextChanged(event: any) {
		this.inputText$.next(event.value);
	}

	importLinkConfiguration() {
    // Add this function to block click propagation from SweetAlert
    const blockDxPopupClose = () => {
        document.querySelectorAll('.swal2-container').forEach(el => {
            el.addEventListener('mousedown', e => e.stopPropagation());
            el.addEventListener('mouseup', e => e.stopPropagation());
            el.addEventListener('click', e => e.stopPropagation());
        });
    };

    Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to update this link using the provided transfer code?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it',
        cancelButtonText: 'Cancel',
        focusCancel: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: blockDxPopupClose // stop dx-popup from closing
    	}).then((result) => {
			if (result.isConfirmed) {
				const payload: LinkTransferDeserializerOptions = this.importLinkConfigForm.value;
				payload.linkId = this.link.id;
				payload.serializedLink = this.inputText$.value;

				this.linktransferService.updateLinkUsingTransferCode(payload).pipe(
					catchError(err => {
						Swal.fire({
							icon: 'error',
							title: 'Update Failed',
							text: err?.message || 'An unexpected error occurred.',
							allowOutsideClick: false,
							didOpen: blockDxPopupClose
						});
						return throwError(() => err);
					})
				).subscribe({
					next: () => {
						Swal.fire({
							icon: 'success',
							title: 'Link Updated',
							text: 'The link was updated successfully, reloading the page.',
							allowOutsideClick: false,
							didOpen: blockDxPopupClose
						}).then(() => {
							location.reload();
						});
					}
				});
			}
		});
	}

	resetWarningsVisbility() {
		this.warningModalMessageService.clearMessageLinks(this.link.id).subscribe({next: r => {
			Swal.fire({
				icon: 'success',
				title: 'Warnings reset',
				text: 'Successfully reset warnings visibility.'
			});
		}, error: showErrorSwal});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
	}
}
